import PageTitle from '../components/common/PageTitle';
import { Award, Calendar, Clock, Users, Shield, BookOpen, Mail } from 'lucide-react';

const AboutPage = () => {
  return (
    <>
      <PageTitle 
        title="O nas" 
        subtitle="Spoznajte naše društvo, zgodovino in poslanstvo"
        backgroundImage="/images/about-hero.jpg"
      />

      {/* History Section */}
      <section className="page-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-title text-secondary">Zgodovina društva</h2>
              <p className="mt-6 text-gray-700">
                Prostovoljno gasilsko društvo Zgornja Polskava ima bogato zgodovino, ki sega v leto 1906. 
                Ustanovljeno je bilo z namenom zaščite lokalnega prebivalstva pred požari in drugimi nesrečami.
              </p>
              <p className="mt-4 text-gray-700">
                Skozi več kot stoletje delovanja se je društvo razvijalo in širilo svoje aktivnosti. 
                Od skromnih začetkov z osnovno opremo smo prerasli v sodobno opremljeno gasilsko enoto.
              </p>
              <p className="mt-4 text-gray-700">
                V naši zgodovini je zapisanih mnogo uspešnih intervencij, požrtvovalnih dejanj, skupnostnih 
                prizadevanj in tovarištva, ki so oblikovali društvo v to, kar je danes.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-8">
                <div className="flex items-center">
                  <Calendar className="text-primary h-6 w-6 mr-2" />
                  <div>
                    <p className="text-2xl font-bold text-secondary">1906</p>
                    <p className="text-sm text-gray-600">Leto ustanovitve</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Users className="text-primary h-6 w-6 mr-2" />
                  <div>
                    <p className="text-2xl font-bold text-secondary">50+</p>
                    <p className="text-sm text-gray-600">Aktivnih članov</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="text-primary h-6 w-6 mr-2" />
                  <div>
                    <p className="text-2xl font-bold text-secondary">24/7</p>
                    <p className="text-sm text-gray-600">Na voljo za pomoč</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/images/history-photo.jpg" 
                alt="Zgodovina PGD Zgornja Polskava" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary text-white p-4 rounded-lg shadow-md hidden md:block">
                <p className="text-2xl font-bold">117+ let</p>
                <p>tradicije in izkušenj</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="page-section bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="section-title text-secondary">Naše poslanstvo</h2>
            <p className="mt-6 text-lg text-gray-700">
              Naše poslanstvo je zagotavljanje varnosti in pomoči lokalni skupnosti 
              s profesionalnim pristopom, izobraževanjem in preventivnimi dejavnostmi.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Shield className="text-primary h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Zaščita</h3>
              <p className="text-gray-600">
                Naša primarna naloga je zaščita življenj, premoženja in okolja pred požari, 
                naravnimi in drugimi nesrečami.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <BookOpen className="text-primary h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Izobraževanje</h3>
              <p className="text-gray-600">
                Zavezani smo k nenehnemu izobraževanju in usposabljanju naših članov ter 
                ozaveščanju javnosti o požarni varnosti.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Users className="text-primary h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Skupnost</h3>
              <p className="text-gray-600">
                Krepimo občutek pripadnosti skupnosti in sodelujemo z drugimi organizacijami 
                za dobrobit vseh občanov.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="page-section">
        <div className="container-custom">
          <h2 className="section-title text-center mb-12 text-secondary">Naše vodstvo</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Team member 1 */}
            <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              <img 
                src="/images/team-1.jpg" 
                alt="Janez Novak - Predsednik" 
                className="w-full h-64 object-cover object-center" 
              />
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Janez Novak</h3>
                <p className="text-primary font-medium mb-3">Predsednik</p>
                <p className="text-gray-600 mb-4">
                  Predsednik društva z več kot 20 leti izkušenj v gasilstvu. Vodi društvo od leta 2018.
                </p>
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-gray-500 flex items-center">
                    <Mail className="h-4 w-4 mr-2" />
                    janez.novak@pgd-zpolskava.si
                  </p>
                </div>
              </div>
            </div>

            {/* Team member 2 */}
            <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              <img 
                src="/images/team-2.jpg" 
                alt="Marko Kovač - Poveljnik" 
                className="w-full h-64 object-cover object-center" 
              />
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Marko Kovač</h3>
                <p className="text-primary font-medium mb-3">Poveljnik</p>
                <p className="text-gray-600 mb-4">
                  Poveljnik z bogatimi operativnimi izkušnjami. Odgovoren za tehnično in operativno pripravljenost enote.
                </p>
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-gray-500 flex items-center">
                    <Mail className="h-4 w-4 mr-2" />
                    marko.kovac@pgd-zpolskava.si
                  </p>
                </div>
              </div>
            </div>

            {/* Team member 3 */}
            <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              <img 
                src="/images/team-3.jpg" 
                alt="Ana Krajnc - Tajnica" 
                className="w-full h-64 object-cover object-center" 
              />
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Ana Krajnc</h3>
                <p className="text-primary font-medium mb-3">Tajnica</p>
                <p className="text-gray-600 mb-4">
                  Skrbi za administrativno delo društva, organizacijo dogodkov in komunikacijo s člani.
                </p>
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-gray-500 flex items-center">
                    <Mail className="h-4 w-4 mr-2" />
                    ana.krajnc@pgd-zpolskava.si
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="page-section bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-title text-secondary">Priznanja in dosežki</h2>
              <p className="mt-6 text-gray-700">
                Skozi desetletja delovanja je naše društvo prejelo številna priznanja za svoje delo 
                in dosežke na področju gasilstva in zaščite pred naravnimi nesrečami.
              </p>
              
              <ul className="mt-8 space-y-4">
                <li className="flex">
                  <Award className="text-primary h-6 w-6 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Zlata plaketa Gasilske zveze Slovenije (2019)</p>
                    <p className="text-gray-600">Za izjemen prispevek k razvoju gasilstva</p>
                  </div>
                </li>
                <li className="flex">
                  <Award className="text-primary h-6 w-6 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Priznanje občine za požrtvovalno delo (2018)</p>
                    <p className="text-gray-600">Za hitro in učinkovito posredovanje ob poplavah</p>
                  </div>
                </li>
                <li className="flex">
                  <Award className="text-primary h-6 w-6 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Srebrni znak Civilne zaščite (2015)</p>
                    <p className="text-gray-600">Za uspešno sodelovanje pri zaščiti in reševanju</p>
                  </div>
                </li>
                <li className="flex">
                  <Award className="text-primary h-6 w-6 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Priznanje za 100 let delovanja (2006)</p>
                    <p className="text-gray-600">Ob stoletnici neprekinjenega delovanja društva</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="/images/award-1.jpg" 
                alt="Priznanje" 
                className="rounded-xl shadow-md"
              />
              <img 
                src="/images/award-2.jpg" 
                alt="Priznanje" 
                className="rounded-xl shadow-md"
              />
              <img 
                src="/images/award-3.jpg" 
                alt="Priznanje" 
                className="rounded-xl shadow-md"
              />
              <img 
                src="/images/award-4.jpg" 
                alt="Priznanje" 
                className="rounded-xl shadow-md"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-white py-16">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Pridruži se nam</h2>
          <p className="text-lg max-w-3xl mx-auto mb-8">
            Odprti smo za nove člane, ki si želijo pomagati skupnosti in postati del naše gasilske družine.
            Ne glede na tvoje izkušnje, lahko najdeš svoje mesto pri nas.
          </p>
          <a 
            href="/kontakt" 
            className="bg-white text-primary hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg inline-flex items-center transition-colors"
          >
            Kontaktiraj nas
          </a>
        </div>
      </section>
    </>
  );
};

export default AboutPage;